<template>
  <div class="relative">
    <div
      v-if="showRawMessages"
      class="mb-2"
    >
      <div class="rounded-lg bg-gray-900 p-4">
        <VueJsonPretty
          :data="message"
          :deep="2"
          :show-double-quotes="false"
          :show-length="true"
          class="!bg-transparent"
          :showLine="false"
          :collapsed-on-click-brackets="true"
          :path-selectable="true"
        />
      </div>
    </div>
    <div class="flex gap-x-2 sm:gap-x-4">
      <!-- AI Avatar -->
      <div
        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100"
      >
        <span class="text-xs font-medium text-purple-600">AI</span>
      </div>
      <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
        <div
          class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
          data-cy="ai-message"
        >
          <!-- Verdict badge -->
          <div
            v-if="verdictChunks.length > 0"
            class="flex w-fit items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5"
          >
            <Scale class="h-3.5 w-3.5 shrink-0 text-gray-600" />
            <span class="text-xs font-semibold text-gray-600">
              {{ t('SupportedByVerifiedVerdicts') }}
            </span>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            ref="proseContent"
            class="prose prose-sm max-w-none overflow-auto text-sm text-gray-800 dark:prose-invert prose-p:my-1
              prose-pre:border prose-pre:border-neutral-200 prose-pre:bg-neutral-100 prose-ol:my-0.5
              prose-ol:list-decimal prose-ol:pl-5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-5 prose-li:my-0.5
              prose-li:pl-2 prose-li:marker:text-gray-400 dark:text-white dark:prose-pre:border-neutral-700
              dark:prose-pre:bg-neutral-800 dark:prose-li:marker:text-gray-500 [&_a:hover]:text-purple-700
              [&_a]:text-purple-600 [&_a]:no-underline"
            v-html="renderedContent"
          />

          <!-- Verdict list -->
          <div
            v-if="verdictChunks.length > 0"
            class="space-y-1.5"
          >
            <p class="text-sm font-semibold text-gray-800">{{ t('VerifiedVerdicts') }}:</p>
            <div
              v-for="(verdict, idx) in verdictChunks"
              :key="verdict.court_judgement_uuid"
            >
              <!-- Error state -->
              <div
                v-if="pdfErrorUuids.includes(verdict.court_judgement_uuid)"
                class="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2.5 py-1 text-sm text-red-700"
              >
                <span class="flex-1 text-xs">{{ t('VerdictPdfError') }}</span>
                <button
                  class="text-xs font-medium underline hover:no-underline"
                  @click="openVerdictPdf(verdict.court_judgement_uuid)"
                >
                  {{ t('VerdictPdfRetry') }}
                </button>
              </div>

              <!-- Normal row -->
              <div
                v-else
                class="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 py-1 pl-1.5 pr-2 hover:bg-gray-100"
              >
                <button
                  class="flex-1 truncate text-left text-sm font-medium text-gray-900 underline decoration-gray-400"
                  @click="openVerdictPdf(verdict.court_judgement_uuid)"
                >
                  [{{ idx + 1 }}] {{ formatVerdictLabel(verdict) }}
                </button>
                <button
                  class="shrink-0 text-gray-400 hover:text-gray-700"
                  @click="downloadVerdictPdf(verdict.court_judgement_uuid, verdict.file_number)"
                >
                  <Download class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Verdict retrieval error notice -->
        <div
          v-if="message.verdict_retrieval_error"
          class="flex max-w-2xl items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm
            text-amber-800"
        >
          <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0" />
          <span>{{ t('VerdictRetrievalError') }}</span>
        </div>

        <!-- Verdict activation hint -->
        <VerdictActivationHint
          v-if="verdictHintVisible"
          @enable="enableCourtDb"
          @reask="handleReask"
          @close="verdictHintVisible = false"
        />
        <!-- AI Message Toolbar (hidden during streaming) -->
        <AiMessageToolbar
          v-if="!isStreaming"
          :message="message"
          @save-snippet="openSaveSnippetDialog"
          @rated="onRated"
        />
      </div>
    </div>

    <!-- Feedback Dialog -->
    <div
      v-if="showFeedbackDialog"
      ref="feedbackDialogRef"
      class="mt-4 max-w-2xl rounded-lg bg-purple-200/50 p-3"
    >
      <div class="mb-3 flex items-start justify-between">
        <div class="flex items-center gap-2">
          <CheckIcon class="h-4 w-4 text-purple-600" />
          <span class="text-sm text-purple-600">
            {{ t('RatingSaved') }}
          </span>
        </div>
        <button
          class="text-purple-400 hover:text-purple-600"
          @click="closeFeedbackDialog"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <div class="rounded-lg bg-white p-4">
        <h3 class="mb-2 text-sm font-semibold text-gray-900">
          {{ rating === 'positive' ? t('WhatDidYouLike') : t('WhatDidntYouLike') }}
        </h3>
        <p class="mb-4 text-sm text-gray-600">
          {{
            rating === 'positive' ? t('FeedbackHelpsImprove') : t('FeedbackHelpsImproveNegative')
          }}
        </p>

        <!-- Issue selection for negative feedback -->
        <div
          v-if="rating === 'negative'"
          class="mb-4"
        >
          <select
            v-model="selectedIssue"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
              focus:outline-none focus:ring-2 focus:ring-purple-500"
            :placeholder="t('SelectIssue')"
          >
            <option
              value=""
              disabled
            >
              {{ t('SelectIssue') }}
            </option>
            <option value="incorrect_information">
              {{ t('IncorrectOrOutdatedInformation') }}
            </option>
            <option value="incomplete_response">
              {{ t('IncompleteResponse') }}
            </option>
            <option value="did_not_follow_instructions">
              {{ t('DidNotFollowInstructions') }}
            </option>
            <option value="inappropriate_style">
              {{ t('InappropriateStyleOrFormatting') }}
            </option>
            <option value="other">
              {{ t('Other') }}
            </option>
          </select>
        </div>

        <div class="mb-4">
          <textarea
            v-model="feedbackComment"
            rows="4"
            class="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
              focus:outline-none focus:ring-2 focus:ring-purple-500"
            :placeholder="rating === 'positive' ? t('YourFeedback') : t('HowCouldThisBeImproved')"
          ></textarea>
        </div>

        <div class="flex justify-end">
          <button
            :disabled="!feedbackComment.trim() || (rating === 'negative' && !selectedIssue)"
            class="rounded bg-purple-600 px-4 py-1.5 text-sm text-white transition-colors hover:bg-purple-700
              disabled:opacity-50"
            @click="submitFeedback"
          >
            {{ t('SendFeedback') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Preference Suggestion -->
    <PreferenceSuggestion
      v-if="message.preference_suggestion"
      ref="preferenceSuggestionRef"
      class="mt-4"
      :suggestion="message.preference_suggestion"
      :session-type="sessionType"
      @dismissed="handleSuggestionDismissed"
      @saved="handleSuggestionSaved"
      @view-in-library="handleViewInLibrary"
    />
  </div>

  <!-- Inline verdict citation floating popover -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="hoveredCitation"
        class="z-[200] w-80 overflow-hidden rounded-xl border border-gray-200 bg-white
          shadow-[0_8px_12px_rgba(0,0,0,0.08),0_4px_8px_rgba(0,0,0,0.08)]"
        :style="citationPopoverStyle"
        @mouseenter="keepCitationOpen"
        @mouseleave="scheduleCitationHide"
      >
        <div class="space-y-1.5 px-4 pb-3 pt-4">
          <p class="text-base font-semibold text-gray-900">
            {{ resolveCourtName(hoveredCitation.verdict.court) }}
          </p>
          <p
            v-if="hoveredCitation.verdict.date || hoveredCitation.verdict.file_number"
            class="text-xs text-gray-500"
          >
            {{
              [
                hoveredCitation.verdict.date
                  ? `${t('VerdictUrteilVom')} ${hoveredCitation.verdict.date}`
                  : '',
                hoveredCitation.verdict.file_number
              ]
                .filter(Boolean)
                .join(' – ')
            }}
          </p>
        </div>
        <div class="relative px-4 pb-4">
          <p class="line-clamp-5 text-sm italic text-gray-700">
            {{ hoveredCitation.verdict.excerpt }}
          </p>
          <div
            class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent"
          />
        </div>
        <div class="px-4 pb-4">
          <button
            class="text-sm font-medium text-gray-900 underline hover:no-underline"
            @click="openVerdictPdf(hoveredCitation!.verdict.court_judgement_uuid)"
          >
            {{ t('VerdictReadMore') }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Save Response Dialog -->
  <Teleport to="body">
    <SaveSnippetDialog
      v-if="showSaveSnippetDialog"
      :content="getMessageContent()"
      :case-uuid="caseStore.caseRecord?.uuid"
      @close="showSaveSnippetDialog = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useMetrics } from '@/composables/useMetrics'
  import {
    type AIChatSessionMessage,
    AIChatSessionTypeEnum,
    EventNameEnum
  } from '@/types/generated'
  import { FeedbackService } from '@/types/generated/services.gen'
  import { OpenAPI } from '@/types/generated/core/OpenAPI'
  import { CheckIcon } from '@heroicons/vue/24/outline'
  import { AlertTriangle, Download, Scale, XIcon } from 'lucide-vue-next'
  import DOMPurify from 'dompurify'
  import MarkdownIt from 'markdown-it'
  import { computed, inject, nextTick, onMounted, onUnmounted, ref, type Ref, watch } from 'vue'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import AiMessageToolbar from './AiMessageToolbar.vue'
  import PreferenceSuggestion from './PreferenceSuggestion.vue'
  import SaveSnippetDialog from './SaveSnippetDialog.vue'
  import VerdictActivationHint from './VerdictActivationHint.vue'

  const props = defineProps<{
    message: AIChatSessionMessage & { _isStreaming?: boolean }
    isLatestMessage?: boolean
  }>()

  const emit = defineEmits<{
    openPreferencesDialog: []
    'document-click': [document: any]
    'update-message': [message: AIChatSessionMessage]
  }>()

  const { t } = useTranslation({
    en: {
      Page: 'Page',
      Pages: 'Pages',
      CopyToClipboard: 'Copy to clipboard',
      Copy: 'Copy',
      Copied: 'Copied!',
      Helpful: 'Mark as helpful',
      NotHelpful: 'Mark as not helpful',
      MarkedHelpful: 'Marked as helpful',
      MarkedNotHelpful: 'Marked as not helpful',
      SaveResponse: 'Save snippet',
      ResponseSaved: 'Snippet saved',
      Saved: 'Saved',
      RatingSaved: 'Would you like to provide additional feedback?',
      WhatDidYouLike: 'What did you like about the response?',
      WhatDidntYouLike: "What didn't you like about the response?",
      FeedbackHelpsImprove: 'Your feedback helps us improve the quality of the AI responses',
      FeedbackHelpsImproveNegative:
        'Your feedback helps us understand what went wrong and improve future responses',
      YourFeedback: 'Your feedback',
      HowCouldThisBeImproved: 'How could this response be improved?',
      SendFeedback: 'Send feedback',
      SelectIssue: 'Select the issue you encountered',
      IncorrectOrOutdatedInformation: 'Incorrect or outdated information',
      IncompleteResponse: 'Incomplete response',
      DidNotFollowInstructions: 'Did not follow instructions',
      InappropriateStyleOrFormatting: 'Inappropriate style or formatting',
      Other: 'Other',
      SupportedByVerifiedVerdicts: 'Supported by verified verdicts',
      VerifiedVerdicts: 'Verified verdicts',
      VerdictUrteilVom: 'Urteil vom',
      VerdictPdfError: 'Could not load verdict PDF',
      VerdictPdfRetry: 'Retry',
      VerdictReadMore: 'Read more',
      VerdictUnknownCourt: 'Unknown court',
      VerdictRetrievalError:
        'Court decision retrieval failed — this answer is based on general knowledge only.'
    },
    de: {
      Page: 'Seite',
      Pages: 'Seiten',
      CopyToClipboard: 'In die Zwischenablage kopieren',
      Copy: 'Kopieren',
      Copied: 'Kopiert!',
      Helpful: 'Als hilfreich markieren',
      NotHelpful: 'Als nicht hilfreich markieren',
      MarkedHelpful: 'Als hilfreich markiert',
      MarkedNotHelpful: 'Als nicht hilfreich markiert',
      SaveResponse: 'Snippet speichern',
      ResponseSaved: 'Snippet gespeichert',
      Saved: 'Gespeichert',
      RatingSaved: 'Bewertung gespeichert. Möchten Sie zusätzliches Feedback geben?',
      WhatDidYouLike: 'Was hat Ihnen an der Antwort gefallen?',
      WhatDidntYouLike: 'Was hat Ihnen an der Antwort nicht gefallen?',
      FeedbackHelpsImprove: 'Ihr Feedback hilft uns, die Qualität der KI-Antworten zu verbessern',
      FeedbackHelpsImproveNegative:
        'Ihr Feedback hilft uns zu verstehen, was schief gelaufen ist und zukünftige Antworten zu verbessern',
      YourFeedback: 'Ihr Feedback',
      HowCouldThisBeImproved: 'Wie könnte diese Antwort verbessert werden?',
      SendFeedback: 'Feedback senden',
      SelectIssue: 'Wählen Sie das Problem aus, das Sie angetroffen haben',
      IncorrectOrOutdatedInformation: 'Falsche oder veraltete Informationen',
      IncompleteResponse: 'Unvollständige Antwort',
      DidNotFollowInstructions: 'Anweisungen nicht befolgt',
      InappropriateStyleOrFormatting: 'Unangemessener Stil oder Formatierung',
      Other: 'Andere',
      SupportedByVerifiedVerdicts: 'Gestützt durch verifizierte Urteile',
      VerifiedVerdicts: 'Verifizierte Urteile',
      VerdictUrteilVom: 'Urteil vom',
      VerdictPdfError: 'Urteil-PDF konnte nicht geladen werden',
      VerdictPdfRetry: 'Erneut versuchen',
      VerdictReadMore: 'Mehr lesen',
      VerdictUnknownCourt: 'Unbekanntes Gericht',
      VerdictRetrievalError:
        'Urteilssuche fehlgeschlagen — diese Antwort basiert nur auf allgemeinem Wissen.'
    }
  })

  const { showCard } = useEntityCard()
  const debugPanelRef = inject<Ref<{ showRawMessages?: boolean }> | undefined>('debugPanelRef')
  const showRawMessages = computed(() => debugPanelRef?.value?.showRawMessages || false)

  const streamingText = inject<Ref<string>>('streamingText', ref(''))
  const streamingChunks = inject<Ref<any[]>>('streamingChunks', ref([]))
  const streamingVerdictContext = inject<Ref<any[]>>('streamingVerdictContext', ref([]))
  const courtDbEnabled = inject<Ref<boolean>>('courtDbEnabled', ref(false))
  const setCourtDbEnabled = inject<(value: boolean) => Promise<void>>(
    'setCourtDbEnabled',
    async (value: boolean) => {
      courtDbEnabled.value = value
    }
  )
  const verdictHintShown = inject<Ref<boolean>>('verdictHintShown', ref(false))
  const markVerdictHintShown = inject<() => void>('markVerdictHintShown', () => {})

  // Local visibility for this instance — stays true once the hint has been shown,
  // so the current hint remains visible after markVerdictHintShown() is called.
  const verdictHintVisible = ref(false)
  watch(
    () =>
      props.message.verdict_activation_hint &&
      props.isLatestMessage &&
      !verdictHintShown.value &&
      !courtDbEnabled.value,
    (shouldShow) => {
      if (shouldShow) {
        verdictHintVisible.value = true
        markVerdictHintShown()
      }
    },
    { immediate: true }
  )

  const isStreaming = computed(() => props.message._isStreaming === true)
  const showSaveSnippetDialog = ref(false)
  const responseSaved = ref(false)
  const showFeedbackDialog = ref(false)
  const feedbackComment = ref('')
  const selectedIssue = ref('')
  const feedbackUuid = ref<string | null>(null)
  const feedbackDialogRef = ref<HTMLElement | null>(null)
  const preferenceSuggestionRef = ref<any>(null)
  const rating = ref<'positive' | 'negative' | null>(null)

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const entityRegistry = new Map()
  const documentRegistry = new Map()
  const verdictCitationRegistry = new Map<string, VerdictChunk>()
  let registryCounter = 0

  // ---------------
  // Verdict rendering
  // ---------------

  interface VerdictChunk {
    type: 'verdict'
    court_judgement_uuid: string
    court: string | null
    date: string | null
    file_number: string | null
    excerpt: string | null
  }

  const pdfErrorUuids = ref<string[]>([])

  // Inline citation hover popover
  const hoveredCitation = ref<{ verdict: VerdictChunk; rect: DOMRect } | null>(null)
  let hidePopoverTimer: ReturnType<typeof setTimeout> | null = null

  const showCitationPopover = (num: string, el: Element) => {
    if (hidePopoverTimer) {
      clearTimeout(hidePopoverTimer)
      hidePopoverTimer = null
    }
    const verdict = verdictCitationRegistry.get(num)
    if (!verdict) return
    hoveredCitation.value = { verdict, rect: el.getBoundingClientRect() }
  }

  const scheduleCitationHide = () => {
    hidePopoverTimer = setTimeout(() => {
      hoveredCitation.value = null
    }, 120)
  }

  const keepCitationOpen = () => {
    if (hidePopoverTimer) {
      clearTimeout(hidePopoverTimer)
      hidePopoverTimer = null
    }
  }

  const citationPopoverStyle = computed(() => {
    if (!hoveredCitation.value) return {}
    const { rect } = hoveredCitation.value
    const popoverWidth = 320
    let left = rect.left
    if (left + popoverWidth > window.innerWidth - 8) left = window.innerWidth - popoverWidth - 8
    if (left < 8) left = 8
    return { position: 'fixed' as const, top: `${rect.bottom + 8}px`, left: `${left}px` }
  })

  const verdictChunks = computed<VerdictChunk[]>(() => {
    const messageContent = props.message.content as any
    const chunks = isStreaming.value
      ? streamingChunks.value || []
      : Array.isArray(messageContent?.chunks)
        ? (messageContent.chunks as any[])
        : []

    // Deduplicate by UUID (same verdict may be cited multiple times inline)
    const seen = new Set<string>()
    const unique = chunks.filter((c): c is VerdictChunk => {
      if (c?.type !== 'verdict') return false
      if (seen.has(c.court_judgement_uuid)) return false
      seen.add(c.court_judgement_uuid)
      return true
    })

    if (!isStreaming.value) return unique

    // During streaming, hydrate metadata from the pre-loaded verdict context (keyed by UUID)
    const ctxByUuid = new Map(
      (streamingVerdictContext.value as any[]).map((v) => [v.court_judgement_uuid, v])
    )
    return unique.map((v) => {
      const ctx = ctxByUuid.get(v.court_judgement_uuid)
      return ctx ? { ...v, ...ctx } : v
    })
  })

  const resolveCourtName = (court: string | null): string =>
    court && court !== 'null' ? court : t('VerdictUnknownCourt')

  const formatVerdictLabel = (verdict: VerdictChunk): string => {
    const parts: string[] = []
    parts.push(resolveCourtName(verdict.court))
    if (verdict.date || verdict.file_number) {
      const datePart = verdict.date ? `${t('VerdictUrteilVom')} ${verdict.date}` : ''
      const fnPart = verdict.file_number
        ? verdict.file_number.length > 30
          ? verdict.file_number.slice(0, 30) + '…'
          : verdict.file_number
        : ''
      const combined = [datePart, fnPart].filter(Boolean).join(' – ')
      if (combined) parts.push(combined)
    }
    return parts.join(', ')
  }

  const fetchVerdictPdfUrl = async (uuid: string): Promise<string> => {
    const response = await fetch(`${OpenAPI.BASE}/api/v3/court_judgements/${uuid}/pdf/`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()
    return data.url
  }

  const chatType = computed(() => (sessionType === AIChatSessionTypeEnum.CASE ? 'case' : 'global'))

  const openVerdictPdf = async (uuid: string) => {
    pdfErrorUuids.value = pdfErrorUuids.value.filter((id) => id !== uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      window.open(url, '_blank', 'noopener')
      void trackEvent(EventNameEnum.VERDICT_PDF_OPENED, {
        court_judgement_uuid: uuid,
        chat_type: chatType.value,
        source_provider: 'anita'
      })
    } catch {
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_PREVIEW_FAILED, {
        court_judgement_uuid: uuid,
        chat_type: chatType.value,
        source_provider: 'anita'
      })
    }
  }

  const downloadVerdictPdf = async (uuid: string, fileNumber: string | null) => {
    pdfErrorUuids.value = pdfErrorUuids.value.filter((id) => id !== uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      const fileResponse = await fetch(url)
      if (!fileResponse.ok) throw new Error()
      const blob = await fileResponse.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileNumber ? `${fileNumber}.pdf` : `verdict_${uuid}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOADED, {
        court_judgement_uuid: uuid,
        chat_type: chatType.value,
        source_provider: 'anita'
      })
    } catch {
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOAD_FAILED, {
        court_judgement_uuid: uuid,
        chat_type: chatType.value,
        source_provider: 'anita'
      })
    }
  }

  const handleEntityClick = (entity: any) => {
    showCard(entity, event?.target as HTMLElement)
    window.dispatchEvent(new CustomEvent('entity-click', { detail: entity }))
  }

  const handleDocumentClick = (document: any) => {
    emit('document-click', document)
  }

  const renderIcon = (type: 'user' | 'building' | 'book') => {
    const iconBaseClass = 'inline-block h-2.5 w-2.5 me-1 text-purple-600'
    switch (type) {
      case 'user':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-icon lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`
      case 'building':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building-icon lucide-building"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>`
      case 'book':
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-book-open-icon lucide-book-open"><path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/></svg>`
      default:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="${iconBaseClass}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6l2 2m0 0l-2 2m2-2H6m12 4H6m6 4H6m6-4H6m6 4H6m6-4H6" /></svg>`
    }
  }

  const sanitizeRenderedMarkdown = (html: string): string =>
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'b',
        'i',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'a',
        'span',
        'button',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'hr',
        'svg',
        'path',
        'circle',
        'rect'
      ],
      ALLOWED_ATTR: [
        'class',
        'href',
        'target',
        'rel',
        'data-verdict-ref',
        'data-ref',
        'viewBox',
        'fill',
        'stroke',
        'd',
        'stroke-width',
        'cx',
        'cy',
        'r'
      ]
    })

  const getCitationBadgeHtml = (num: string, ref: string = num): string =>
    `<button class="verdict-citation-badge" data-verdict-ref="${ref}">${num}</button>`

  const processTextCitations = (text: string): string => {
    if (verdictCitationRegistry.size === 0) return text
    return text.replace(/\[(\d+)\]/g, (match, num) =>
      verdictCitationRegistry.has(num) ? getCitationBadgeHtml(num) : match
    )
  }

  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  const getEntityHtml = (entity: any) => {
    const iconHtml = renderIcon(entity.is_company ? 'building' : 'user')
    const key = `entity-${registryCounter++}`
    entityRegistry.set(key, entity)
    return `<span class="entity-link inline-flex cursor-pointer items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">${iconHtml}${escapeHtml(entity.name)}</span>`
  }

  const getDocumentHtml = (doc: any) => {
    const iconHtml = renderIcon('book')
    const key = `doc-${registryCounter++}`
    documentRegistry.set(key, doc)
    const pageText =
      doc.pages.length === 0
        ? ''
        : doc.pages.length === 1
          ? `${t('Page')} ${escapeHtml(String(doc.pages[0]))}`
          : `${t('Pages')}: ${doc.pages.map((p: any) => escapeHtml(String(p))).join(', ')}`
    return `<span class="document-link inline-flex cursor-pointer items-center gap-x-1 rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">
      ${iconHtml}${escapeHtml(doc.title)}${pageText ? ` <span class="text-gray-500">(${pageText})</span>` : ''}
    </span>`
  }

  const { trackEvent } = useMetrics()
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const reaskWithCourtDb = inject<() => Promise<void>>('reaskWithCourtDb', async () => {})

  const enableCourtDb = () => {
    setCourtDbEnabled(true)
  }

  const handleReask = () => {
    reaskWithCourtDb()
  }

  // Scroll to element with smooth animation
  const scrollToElement = (element: HTMLElement) => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    })
  }

  const submitFeedback = async () => {
    if (feedbackUuid.value && feedbackComment.value.trim()) {
      try {
        // Prepare comment with issue type for negative feedback
        let comment = feedbackComment.value.trim()
        if (rating.value === 'negative' && selectedIssue.value) {
          const issueText = t(selectedIssue.value)
          comment = `Issue: ${issueText}\n\nFeedback: ${comment}`
        }

        // Update existing feedback with comment
        const updatedFeedback = await FeedbackService.feedbackPartialUpdate({
          uuid: feedbackUuid.value,
          requestBody: {
            comment: comment
          }
        })

        // Check if preference suggestion was generated (for negative feedback)
        if (rating.value === 'negative' && (updatedFeedback as any).preference_suggestion) {
          // Create updated message with preference suggestion
          const updatedMessage = {
            ...props.message,
            preference_suggestion: {
              id: `feedback_suggestion_${Date.now()}`,
              title: (updatedFeedback as any).preference_suggestion.title,
              content: (updatedFeedback as any).preference_suggestion.content,
              reasoning: (updatedFeedback as any).preference_suggestion.reasoning,
              suggested_scope: (updatedFeedback as any).preference_suggestion.suggested_scope,
              state: 'PENDING',
              created_preference_uuid: undefined
            }
          }
          emit('update-message', updatedMessage)
        }
      } catch (error) {
        console.error('Failed to update feedback:', error)
      }
    }
    closeFeedbackDialog()
  }

  const closeFeedbackDialog = () => {
    showFeedbackDialog.value = false
    feedbackComment.value = ''
    selectedIssue.value = ''
    // Reset for next rating
    setTimeout(() => {
      rating.value = null
      feedbackUuid.value = null
    }, 100)
  }

  const onRated = ({
    value,
    feedback_uuid
  }: {
    value: 'positive' | 'negative'
    feedback_uuid?: string
  }) => {
    rating.value = value
    feedbackUuid.value = feedback_uuid ?? null
    showFeedbackDialog.value = true
  }

  const openSaveSnippetDialog = () => {
    showSaveSnippetDialog.value = true

    // Listen for the dialog close event to detect if a response was saved
    const handleDialogClose = () => {
      // Check if the response was saved (this would be set by the SaveResponseDialog component)
      // For now, we'll simulate this by always showing the saved state temporarily
      responseSaved.value = true

      // Reset the saved state after a delay
      setTimeout(() => {
        responseSaved.value = false
      }, 2000)
    }

    // Call the handler when the dialog is closed
    setTimeout(() => {
      const dialogCloseListener = () => {
        handleDialogClose()
        window.removeEventListener('save-response-dialog-closed', dialogCloseListener)
      }
      window.addEventListener('save-response-dialog-closed', dialogCloseListener, { once: true })
    }, 100)
  }

  const getMessageContent = () => {
    const messageContentObj = props.message.content as any
    if (!messageContentObj?.chunks) return ''

    const chunks = Array.isArray(messageContentObj?.chunks)
      ? (messageContentObj?.chunks as any[])
      : []

    let raw = ''
    for (const chunk of chunks) {
      if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue

      switch (chunk.type) {
        case 'text':
          raw += chunk.text ?? ''
          break
        case 'entity':
          raw += chunk.name ?? ''
          break
        case 'document':
          raw += `${chunk.title} (${chunk.pages.length === 1 ? t('Page') + ' ' + chunk.pages[0] : t('Pages') + ': ' + chunk.pages.join(', ')})`
          break
        default:
          break
      }
    }

    return raw
  }

  const renderedContent = computed(() => {
    entityRegistry.clear()
    documentRegistry.clear()
    verdictCitationRegistry.clear()
    registryCounter = 0

    if (isStreaming.value) {
      const completedChunks = streamingChunks.value || []
      const liveText = streamingText.value || ''

      if (completedChunks.length === 0 && !liveText)
        return ''

        // Build verdict citation registry from pre-loaded context (keyed by UUID)
      ;(streamingVerdictContext.value as any[]).forEach((v) => {
        verdictCitationRegistry.set(v.court_judgement_uuid, v as VerdictChunk)
      })

      const streamingUuidToNum = new Map<string, number>()
      let raw = ''
      for (const chunk of completedChunks) {
        if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue
        switch (chunk.type) {
          case 'text':
            raw += processTextCitations(chunk.text ?? '')
            break
          case 'verdict': {
            const uuid = (chunk as VerdictChunk).court_judgement_uuid
            if (!streamingUuidToNum.has(uuid))
              streamingUuidToNum.set(uuid, streamingUuidToNum.size + 1)
            raw += getCitationBadgeHtml(String(streamingUuidToNum.get(uuid)!), uuid)
            break
          }
          case 'entity':
            raw += getEntityHtml(chunk)
            break
          case 'document':
            raw += getDocumentHtml(chunk)
            break
        }
      }

      if (liveText) {
        raw += processTextCitations(liveText) + '<span class="streaming-cursor"></span>'
      }

      return sanitizeRenderedMarkdown(md.render(raw))
    }

    const savedContent = props.message.content as any
    if (!savedContent?.chunks) return ''

    const chunks = Array.isArray(savedContent?.chunks) ? (savedContent?.chunks as any[]) : []

    // Build verdict citation registry (keyed by UUID; also by index for backward-compat with saved messages)
    const uuidToNum = new Map<string, number>()
    let verdictIdx = 0
    for (const chunk of chunks) {
      if (chunk?.type === 'verdict') {
        const v = chunk as VerdictChunk
        const uuid = v.court_judgement_uuid
        if (!uuidToNum.has(uuid)) {
          verdictIdx++
          uuidToNum.set(uuid, verdictIdx)
          verdictCitationRegistry.set(String(verdictIdx), v) // backward-compat: old messages have [N] in text
        }
        verdictCitationRegistry.set(uuid, v)
      }
    }

    let raw = ''
    for (const chunk of chunks) {
      if (!chunk || typeof chunk !== 'object' || !('type' in chunk)) continue

      switch (chunk.type) {
        case 'text':
          raw += processTextCitations(chunk.text ?? '')
          break
        case 'verdict': {
          const uuid = (chunk as VerdictChunk).court_judgement_uuid
          const num = uuidToNum.get(uuid)
          if (num !== undefined) raw += getCitationBadgeHtml(String(num), uuid)
          break
        }
        case 'entity':
          raw += getEntityHtml(chunk)
          break
        case 'document':
          raw += getDocumentHtml(chunk)
          break
        default:
          console.error('Unknown chunk type:', chunk.type)
          break
      }
    }

    return sanitizeRenderedMarkdown(md.render(raw))
  })

  const proseContent = ref<HTMLElement | null>(null)

  const handleContainerMouseOver = (e: Event) => {
    const badge = (e.target as HTMLElement).closest('.verdict-citation-badge')
    if (badge) {
      const ref = badge.getAttribute('data-verdict-ref')
      if (ref) showCitationPopover(ref, badge)
    }
  }

  const handleContainerMouseOut = (e: Event) => {
    const badge = (e.target as HTMLElement).closest('.verdict-citation-badge')
    if (badge) scheduleCitationHide()
  }

  watch(
    () => (props.message.content as any)?.chunks,
    () => {
      nextTick(() => {
        if (proseContent.value) {
          proseContent.value.removeEventListener('click', handleContainerClick)
          proseContent.value.addEventListener('click', handleContainerClick)
          proseContent.value.removeEventListener('mouseover', handleContainerMouseOver)
          proseContent.value.addEventListener('mouseover', handleContainerMouseOver)
          proseContent.value.removeEventListener('mouseout', handleContainerMouseOut)
          proseContent.value.addEventListener('mouseout', handleContainerMouseOut)
        }
      })
    },
    { deep: true }
  )

  // Watch for preference suggestion changes and auto-scroll
  watch(
    () => props.message.preference_suggestion,
    (newSuggestion) => {
      if (newSuggestion) {
        nextTick(() => {
          if (preferenceSuggestionRef.value) {
            // For Vue components, we need to access the $el property
            const element = preferenceSuggestionRef.value.$el || preferenceSuggestionRef.value
            scrollToElement(element)
          }
        })
      }
    }
  )

  const handleContainerClick = (e: Event) => {
    const target = e.target as HTMLElement
    const entityLink = target.closest('.entity-link')
    const documentLink = target.closest('.document-link')
    const citationBadge = target.closest('.verdict-citation-badge')

    if (citationBadge) {
      const ref = citationBadge.getAttribute('data-verdict-ref')
      if (ref) {
        const verdict = verdictCitationRegistry.get(ref)
        if (verdict) openVerdictPdf(verdict.court_judgement_uuid)
      }
    }

    if (entityLink) {
      const ref = entityLink.getAttribute('data-ref')
      if (ref) {
        const entity = entityRegistry.get(ref)
        if (entity) {
          handleEntityClick(entity)
        }
      }
    }

    if (documentLink) {
      const ref = documentLink.getAttribute('data-ref')
      if (ref) {
        const doc = documentRegistry.get(ref)
        if (doc) {
          emit('document-click', doc)
        }
      }
    }
  }

  onMounted(() => {
    if (proseContent.value) {
      proseContent.value.addEventListener('click', handleContainerClick)
      proseContent.value.addEventListener('mouseover', handleContainerMouseOver)
      proseContent.value.addEventListener('mouseout', handleContainerMouseOut)
    }
  })

  onUnmounted(() => {
    if (proseContent.value) {
      proseContent.value.removeEventListener('click', handleContainerClick)
      proseContent.value.removeEventListener('mouseover', handleContainerMouseOver)
      proseContent.value.removeEventListener('mouseout', handleContainerMouseOut)
    }
    if (hidePopoverTimer) clearTimeout(hidePopoverTimer)
  })

  const caseStore = useCaseStore()

  // Preference suggestion handlers
  const handleSuggestionDismissed = () => {
    console.log('Preference suggestion dismissed')
  }

  const handleSuggestionSaved = (preference: any) => {
    console.log('Preference suggestion saved:', preference)
    // Could emit an event to parent to refresh preferences or show success message
  }

  const handleViewInLibrary = () => {
    console.log('View in preference library clicked')
    emit('openPreferencesDialog')
  }
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: scale(1);
  }
</style>

<style>
  .streaming-cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    background-color: rgb(124 58 237); /* tailwind violet-600 */
    margin-left: 1px;
    vertical-align: text-bottom;
    animation: blink 1s step-end infinite;
  }

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }

  .prose :deep(p) {
    margin: 0;
    display: inline;
  }

  .prose :deep(ul),
  .prose :deep(ol) {
    margin: 0;
    padding-left: 1.5em;
  }

  .prose :deep(li) {
    margin: 0;
  }

  .prose :deep(li:not(:last-child)) {
    margin-bottom: 0.25em;
  }
</style>

<style>
  .verdict-citation-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 9999px;
    background-color: rgb(124 58 237);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    vertical-align: middle;
    margin: 0 1px;
    border: none;
    padding: 0;
    transition: background-color 150ms;
    flex-shrink: 0;
  }

  .verdict-citation-badge:hover {
    background-color: rgb(109 40 217);
  }
</style>

<style scoped>
  :deep(.vjs-tree) {
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    background-color: transparent !important;
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__string) {
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__number) {
    color: rgb(129 140 248) !important;
  }

  :deep(.vjs-tree .vjs-value__boolean) {
    color: rgb(244 114 182) !important;
  }

  :deep(.vjs-tree .vjs-value__null) {
    color: rgb(248 113 113) !important;
  }

  :deep(.vjs-tree .vjs-key) {
    color: rgb(139 92 246) !important;
  }
</style>
